// ########### LIGHTENED ###########
import { useEffect, useMemo, useState } from "react";
import * as d3geo from "d3-geo";

export default function KansasCountiesInteractive({
  url = "/data/ks-counties.geojson",
  width = 900,
  height = 800,
  countyNameField = "NAME",
  primary = "#1E2A38",
  westFill = "#FCFBF6",
  westStroke = "#D8D8D8",
  westStrokeWidth = 1,
}) {
  const [features, setFeatures] = useState([]);
  const [refLon, setRefLon] = useState(null);
  const [hover, setHover] = useState(null);
  const [viewBox, setViewBox] = useState(`0 0 ${width} ${height}`);

  // -------- load features --------
  useEffect(() => {
    (async () => {
      const res = await fetch(url);
      const geo = await res.json();
      const feats = (geo.features || []).filter(
        (f) => f?.geometry && /Polygon$/.test(f.geometry.type)
      );
      setFeatures(feats);

      const norm = (s) => (s || "").trim().toLowerCase();
      const phillips = feats.find(
        (f) => norm(f.properties?.[countyNameField]) === "phillips"
      );
      if (phillips) {
        const [lon] = d3geo.geoCentroid(phillips);
        setRefLon(lon);
      }
    })();
  }, [url, countyNameField]);

  // -------- projection & path --------
  const base = useMemo(() => {
    if (!features.length) return null;
    return d3geo.geoIdentity().reflectY(true).fitSize([width, height], {
      type: "FeatureCollection",
      features,
    });
  }, [features, width, height]);

  const path = useMemo(() => (base ? d3geo.geoPath(base) : null), [base]);

  // -------- compute tight viewBox --------
  useEffect(() => {
    if (!path || !features.length) return;
    const [[x0, y0], [x1, y1]] = path.bounds({
      type: "FeatureCollection",
      features,
    });
    const boxWidth = x1 - x0;
    const boxHeight = y1 - y0;
    setViewBox(`${x0} ${y0} ${boxWidth} ${boxHeight}`);
  }, [path, features]);

  if (!path) return null;

  // -------- render --------
  return (
    <div style={{ position: "relative" }}>
      <svg
        viewBox={viewBox}
        width="100%" // responsive width
        height="100%" // let it stretch with parent container
        preserveAspectRatio="xMidYMid meet"
        className="h-auto max-h-[600px] sm:max-h-[500px] md:max-h-[520px] lg:max-h-[640px]"
      >
        <g>
          {features.map((f, i) => {
            const name = f?.properties?.[countyNameField] || `County ${i + 1}`;
            const [lon] = d3geo.geoCentroid(f);
            const isEast = refLon !== null ? lon >= refLon : true;

            if (!isEast) {
              return (
                <path
                  key={`f-${i}`}
                  d={path(f) || ""}
                  fill={westFill}
                  stroke={westStroke}
                  strokeWidth={westStrokeWidth}
                  style={{ pointerEvents: "none" }}
                />
              );
            }

            return (
              <path
                key={`f-${i}`}
                d={path(f) || ""}
                fill={primary}
                stroke="white"
                strokeWidth={1.5}
                className="cursor-pointer"
                aria-label={name}
                onMouseEnter={(e) => {
                  e.currentTarget.setAttribute("fill", primary);
                  e.currentTarget.setAttribute("fill-opacity", "0.6"); // 70% opacity
                  const bounds =
                    e.currentTarget.ownerSVGElement.getBoundingClientRect();
                  setHover({
                    name,
                    x: e.clientX - bounds.left + 12,
                    y: e.clientY - bounds.top + 12,
                  });
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.setAttribute("fill", primary);
                  e.currentTarget.removeAttribute("fill-opacity");
                  setHover(null);
                }}
                onClick={() => console.log("Selected:", name)}
              />
            );
          })}
        </g>
      </svg>

      {hover && (
        <div
          style={{
            position: "absolute",
            left: hover.x,
            top: hover.y,
            background: "#1E2A38",
            color: "#fff",
            padding: "20px 30px",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: "bold",
            pointerEvents: "none",
            zIndex: 50,
          }}
        >
          {hover.name}
        </div>
      )}
    </div>
  );
}

// import { useEffect, useMemo, useState } from "react";
// import * as d3geo from "d3-geo";

// export default function KansasCountiesInteractive({
//   url = "/data/ks-counties.geojson",
//   width = 900,
//   height = 520,
//   countyNameField = "NAME",
//   primary = "#1E2A38", // your brand base (navy)
//   westFill = "#F7F7F7", // light gray for excluded
//   westStroke = "#E4E4E4", // border color for excluded only
//   westStrokeWidth = 1,
// }) {
//   const [features, setFeatures] = useState([]);
//   const [refLon, setRefLon] = useState(null); // Phillips cutoff
//   const [hover, setHover] = useState(null);
//   const [selected, setSelected] = useState(null);

//   // -------- load + compute Phillips longitude --------
//   useEffect(() => {
//     (async () => {
//       const res = await fetch(url);
//       const geo = await res.json();
//       const feats = (geo.features || []).filter(
//         (f) => f?.geometry && /Polygon$/.test(f.geometry.type)
//       );
//       setFeatures(feats);

//       const norm = (s) => (s || "").trim().toLowerCase();
//       const phillips = feats.find(
//         (f) => norm(f.properties?.[countyNameField]) === "phillips"
//       );
//       if (!phillips) {
//         console.warn("Phillips County not found.");
//         setRefLon(null);
//         return;
//       }
//       const [lon] = d3geo.geoCentroid(phillips);
//       setRefLon(lon);
//     })();
//   }, [url, countyNameField]);

//   // -------- projection & path --------
//   const projection = useMemo(() => {
//     if (!features.length) return null;
//     return d3geo
//       .geoIdentity()
//       .reflectY(true)
//       .fitSize([width, height], { type: "FeatureCollection", features });
//   }, [features, width, height]);

//   const path = useMemo(
//     () => (projection ? d3geo.geoPath(projection) : null),
//     [projection]
//   );

//   // ==================== COLOR SYSTEM ====================

//   // hex <-> HSL helpers
//   const hexToHsl = (hex) => {
//     let r, g, b;
//     const s = hex.replace("#", "").toLowerCase();
//     const v =
//       s.length === 3
//         ? s
//             .split("")
//             .map((c) => c + c)
//             .join("")
//         : s;
//     r = parseInt(v.slice(0, 2), 16) / 255;
//     g = parseInt(v.slice(2, 4), 16) / 255;
//     b = parseInt(v.slice(4, 6), 16) / 255;
//     const max = Math.max(r, g, b),
//       min = Math.min(r, g, b);
//     let h,
//       l = (max + min) / 2,
//       d = max - min,
//       sat;
//     if (d === 0) {
//       h = 0;
//       sat = 0;
//     } else {
//       sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
//       switch (max) {
//         case r:
//           h = (g - b) / d + (g < b ? 6 : 0);
//           break;
//         case g:
//           h = (b - r) / d + 2;
//           break;
//         default:
//           h = (r - g) / d + 4;
//           break;
//       }
//       h /= 6;
//     }
//     return { h: h * 360, s: sat * 100, l: l * 100 };
//   };
//   const hslToHex = ({ h, s, l }) => {
//     h = ((h % 360) + 360) % 360;
//     s = Math.max(0, Math.min(100, s));
//     l = Math.max(0, Math.min(100, l));
//     const C = (1 - Math.abs((2 * l) / 100 - 1)) * (s / 100);
//     const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
//     const m = l / 100 - C / 2;
//     let r = 0,
//       g = 0,
//       b = 0;
//     if (0 <= h && h < 60) [r, g, b] = [C, X, 0];
//     else if (60 <= h && h < 120) [r, g, b] = [X, C, 0];
//     else if (120 <= h && h < 180) [r, g, b] = [0, C, X];
//     else if (180 <= h && h < 240) [r, g, b] = [0, X, C];
//     else if (240 <= h && h < 300) [r, g, b] = [X, 0, C];
//     else [r, g, b] = [C, 0, X];
//     const toHex = (n) =>
//       Math.round((n + m) * 255)
//         .toString(16)
//         .padStart(2, "0");
//     return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
//   };

//   /**
//    * Build a palette around `base` with both shades (darker) and tints (lighter).
//    * - Keeps the base hue.
//    * - Slightly increases saturation for tints (to avoid looking washed out)
//    *   and slightly decreases saturation for shades (to avoid muddy grays).
//    * - Returns: [dark3, dark2, dark1, base, light1, light2, light3]
//    */
//   const buildPaletteFromPrimary = (base = "#1E2A38") => {
//     const { h, s, l } = hexToHsl(base);

//     // choose how many on each side; 3 + base + 3 = 7 total
//     const darkLs = [
//       Math.max(10, l - 24),
//       Math.max(12, l - 18),
//       Math.max(14, l - 12),
//     ];
//     const lightLs = [
//       Math.min(92, l + 10),
//       Math.min(94, l + 18),
//       Math.min(96, l + 24),
//     ];

//     // saturation adjustments to keep colors clean
//     const darkSs = darkLs.map(() => Math.max(20, s - 8)); // a bit desaturated in darks
//     const lightSs = lightLs.map(() => Math.min(90, s + 6)); // a bit more saturated in lights

//     const darks = darkLs.map((L, i) => hslToHex({ h, s: darkSs[i], l: L }));
//     const lights = lightLs.map((L, i) => hslToHex({ h, s: lightSs[i], l: L }));

//     return [...darks, base, ...lights]; // 7-color palette
//   };

//   const palette = useMemo(() => buildPaletteFromPrimary(primary), [primary]);

//   const lighten = (hex, amt = 10) => {
//     const hsl = hexToHsl(hex);
//     return hslToHex({ ...hsl, l: Math.min(98, hsl.l + amt) });
//   };

//   // ---- EAST-only greedy coloring to avoid adjacent duplicates ----
//   const eastColorByIndex = useMemo(() => {
//     if (!features.length || refLon === null) return [];

//     const isEast = features.map((f) => {
//       const [lon] = d3geo.geoCentroid(f);
//       return lon >= refLon; // Phillips included
//     });

//     // gather rings
//     const ringsOf = (g) =>
//       g.type === "Polygon"
//         ? g.coordinates
//         : g.type === "MultiPolygon"
//           ? g.coordinates.flat()
//           : [];

//     // shared-edge index for EAST only
//     const round = (v) => Math.round(v * 1e4) / 1e4;
//     const segKey = ([x1, y1], [x2, y2]) => {
//       const a = `${round(x1)},${round(y1)}`,
//         b = `${round(x2)},${round(y2)}`;
//       return a < b ? `${a}|${b}` : `${b}|${a}`;
//     };
//     const edges = new Map(); // key -> Set(idx)
//     const addSeg = (k, idx) => {
//       if (!edges.has(k)) edges.set(k, new Set());
//       edges.get(k).add(idx);
//     };

//     features.forEach((f, idx) => {
//       if (!isEast[idx]) return;
//       const rings = ringsOf(f.geometry);
//       for (const ring of rings) {
//         for (let i = 0; i < ring.length - 1; i++)
//           addSeg(segKey(ring[i], ring[i + 1]), idx);
//         if (ring.length > 2) addSeg(segKey(ring.at(-1), ring[0]), idx);
//       }
//     });

//     const N = features.length;
//     const adj = Array.from({ length: N }, () => new Set());
//     for (const s of edges.values()) {
//       if (s.size < 2) continue;
//       const list = [...s];
//       for (let i = 0; i < list.length; i++) {
//         for (let j = i + 1; j < list.length; j++) {
//           const a = list[i],
//             b = list[j];
//           adj[a].add(b);
//           adj[b].add(a);
//         }
//       }
//     }

//     const eastIdx = features.map((_, i) => i).filter((i) => isEast[i]);
//     const order = eastIdx.sort((a, b) => adj[b].size - adj[a].size);
//     const colorIdx = Array(N).fill(-1);
//     for (const u of order) {
//       const used = new Set();
//       adj[u].forEach((v) => {
//         if (colorIdx[v] !== -1) used.add(colorIdx[v]);
//       });
//       let c = 0;
//       while (used.has(c) && c < palette.length) c++;
//       colorIdx[u] = c % palette.length;
//     }

//     const out = Array(N).fill(null);
//     for (let i = 0; i < N; i++) if (isEast[i]) out[i] = palette[colorIdx[i]];
//     return out;
//   }, [features, refLon, palette]);

//   // precompute east/west flags for render & overlay
//   const eastFlags = useMemo(() => {
//     if (!features.length || refLon === null) return [];
//     return features.map((f) => {
//       const [lon] = d3geo.geoCentroid(f);
//       return lon >= refLon;
//     });
//   }, [features, refLon]);

//   if (!path) return null;

//   // ==================== RENDER ====================
//   return (
//     <div style={{ position: "relative" }}>
//       <svg
//         viewBox={`0 0 ${width} ${height}`}
//         width="100%"
//         height="auto"
//         role="img"
//         aria-label="Interactive map of Kansas counties"
//       >
//         {/* Fills */}
//         <g>
//           {features.map((f, i) => {
//             const name = f?.properties?.[countyNameField] || `County ${i + 1}`;
//             const isEast = eastFlags.length ? eastFlags[i] : true;

//             if (!isEast) {
//               // WEST: grey fill, non-interactive
//               return (
//                 <path
//                   key={`f-${i}`}
//                   d={path(f) || ""}
//                   fill={westFill}
//                   style={{ pointerEvents: "none" }}
//                   aria-hidden="true"
//                 />
//               );
//             }

//             const base = eastColorByIndex[i] || palette[0];
//             const isSel = selected === name;

//             return (
//               <path
//                 key={`f-${i}`}
//                 d={path(f) || ""}
//                 fill={isSel ? lighten(base, 12) : base}
//                 className="cursor-pointer"
//                 tabIndex={0}
//                 aria-label={name}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.setAttribute("fill", lighten(base, 16));
//                   setHover({ name, x: e.clientX + 12, y: e.clientY + 12 });
//                 }}
//                 onMouseMove={(e) =>
//                   setHover((h) =>
//                     h ? { ...h, x: e.clientX + 12, y: e.clientY + 12 } : h
//                   )
//                 }
//                 onMouseLeave={(e) => {
//                   e.currentTarget.setAttribute(
//                     "fill",
//                     isSel ? lighten(base, 12) : base
//                   );
//                   setHover(null);
//                 }}
//                 onClick={() => setSelected(name)}
//                 onKeyDown={(e) => {
//                   if (e.key === "Enter" || e.key === " ") {
//                     e.preventDefault();
//                     setSelected(name);
//                   }
//                 }}
//               />
//             );
//           })}
//         </g>

//         {/* WEST-ONLY BORDER OVERLAY (stroke on top, no fill) */}
//         {eastFlags.length > 0 && (
//           <g style={{ pointerEvents: "none" }}>
//             {features.map((f, i) =>
//               !eastFlags[i] ? (
//                 <path
//                   key={`ws-${i}`}
//                   d={path(f) || ""}
//                   fill="none"
//                   stroke={westStroke}
//                   strokeWidth={westStrokeWidth}
//                   vectorEffect="non-scaling-stroke"
//                 />
//               ) : null
//             )}
//           </g>
//         )}
//       </svg>

//       {/* Tooltip for EAST counties only */}
//       {hover && (
//         <div
//           style={{
//             position: "fixed",
//             left: hover.x,
//             top: hover.y,
//             background: "rgba(0,0,0,0.85)",
//             color: "#fff",
//             padding: "4px 8px",
//             borderRadius: 6,
//             fontSize: 12,
//             pointerEvents: "none",
//             zIndex: 50,
//           }}
//         >
//           {hover.name}
//         </div>
//       )}
//     </div>
//   );
// }

// ########### SOLID ###########
// import { useEffect, useMemo, useState } from "react";
// import * as d3geo from "d3-geo";

// export default function KansasCountiesMinimal({
//   url = "/data/ks-counties.geojson",
//   width = 900,
//   height = 520,
//   countyNameField = "NAME",
//   // baseFill = "#FEF9E5", // light gray
//   // hoverFill = "#F9DC5C", // brand yellow
//   // stroke = "#F9DC5C", // white borders
//   baseFill = "#F9DC5C", // light gray
//   hoverFill = "#F9DC5C", // brand yellow
//   stroke = "#ffffff", // white borders
//   strokeWidth = 1,
// }) {
//   const [features, setFeatures] = useState([]);
//   const [hover, setHover] = useState(null); // { name, x, y }

//   // Load data, keep only Phillips + east
//   useEffect(() => {
//     (async () => {
//       const res = await fetch(url);
//       const geo = await res.json();
//       const all = (geo.features || []).filter(
//         (f) => f?.geometry && /Polygon$/.test(f.geometry.type)
//       );

//       const norm = (s) => (s || "").trim().toLowerCase();
//       const phillips = all.find(
//         (f) => norm(f.properties?.[countyNameField]) === "phillips"
//       );

//       if (!phillips) {
//         console.warn("Phillips County not found — rendering all counties.");
//         setFeatures(all);
//         return;
//       }

//       const [refLon] = d3geo.geoCentroid(phillips);
//       const east = all.filter((f) => {
//         const [lon] = d3geo.geoCentroid(f);
//         return lon >= refLon; // keep Phillips + east
//       });

//       setFeatures(east);
//     })();
//   }, [url, countyNameField]);

//   // Planar fit (works with your file)
//   const projection = useMemo(() => {
//     if (!features.length) return null;
//     return d3geo
//       .geoIdentity()
//       .reflectY(true)
//       .fitSize([width, height], { type: "FeatureCollection", features });
//   }, [features, width, height]);

//   const path = useMemo(
//     () => (projection ? d3geo.geoPath(projection) : null),
//     [projection]
//   );

//   if (!path) return null;

//   return (
//     <div style={{ position: "relative" }}>
//       <svg
//         viewBox={`0 0 ${width} ${height}`}
//         width="100%"
//         height="auto"
//         role="img"
//         aria-label="Kansas counties (east of Phillips)"
//       >
//         <g>
//           {features.map((f, i) => {
//             const name = f?.properties?.[countyNameField] || `County ${i + 1}`;
//             return (
//               <path
//                 key={i}
//                 d={path(f) || ""}
//                 fill={baseFill}
//                 stroke={stroke}
//                 strokeWidth={strokeWidth}
//                 vectorEffect="non-scaling-stroke"
//                 className="cursor-pointer"
//                 tabIndex={0}
//                 aria-label={name}
//                 onMouseEnter={(e) => {
//                   e.currentTarget.setAttribute("fill", hoverFill);
//                   setHover({ name, x: e.clientX + 12, y: e.clientY + 12 });
//                 }}
//                 onMouseMove={(e) =>
//                   setHover((h) =>
//                     h ? { ...h, x: e.clientX + 12, y: e.clientY + 12 } : h
//                   )
//                 }
//                 onMouseLeave={(e) => {
//                   e.currentTarget.setAttribute("fill", baseFill);
//                   setHover(null);
//                 }}
//               />
//             );
//           })}
//         </g>
//       </svg>

//       {/* Tooltip */}
//       {hover && (
//         <div
//           style={{
//             position: "fixed",
//             left: hover.x,
//             top: hover.y,
//             background: "rgba(0,0,0,0.85)",
//             color: "#fff",
//             padding: "4px 8px",
//             borderRadius: 6,
//             fontSize: 12,
//             pointerEvents: "none",
//             zIndex: 50,
//           }}
//         >
//           {hover.name}
//         </div>
//       )}
//     </div>
//   );
// }
